import _ from 'lodash';
import moment from 'moment';
import React, { useState, useContext, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Button } from 'react-bootstrap';
import { AgGridReact, AgGridColumnProps } from '@ag-grid-community/react';
import {
  AllModules,
  GridApi,
  GridReadyEvent,
  ICellRendererParams,
  GetContextMenuItemsParams,
  ColumnApi,
} from '@ag-grid-enterprise/all-modules';
import { Validator } from 'blaze-validator';
import { AxiosResponse } from 'axios';
import printJS from 'print-js';
import { aggregate } from '../../util/grid';
import ModalContext from './ModalContext';
import { modal } from './ModalEmitter';
import ApiService from '../../services/ApiService';
import getGridOptions from '../list/gridOptions';

import '../../styles/modaltable.scss';

const types = ['currency', 'date', 'email', 'number', 'percent', 'phone'];

interface ModalTableProps {
  controlId?: string;
  data: [any[]];
  emptyMessage: string;
  headers: ModalTableHeader[] | string[];
  hideToolbar?: boolean;
  style: any;
}

interface ModalTableColumn extends AgGridColumnProps {
  columnIndex: number;
}

interface ModalTableHeader extends AgGridColumnProps {
  aggregate?: string;
  className?: string;
  defaultValue?: string;
  helper?: string;
  text?: string;
  type?: string;
}

const convertToTableHeaders = (headers: ModalTableHeader[] | string[] = []): ModalTableHeader[] => {
  const result: ModalTableHeader[] = [];

  headers.forEach((header: ModalTableHeader | string) => {
    if (typeof header === 'string') {
      result.push({ text: header });
      return;
    }

    result.push(header);
  });

  return result;
};

const ModalTable = ({
  headers = [],
  data = [[]],
  hideToolbar = false,
  style = {},
  emptyMessage = 'Empty',
  controlId = '',
}: ModalTableProps) => {
  const [gridApi, setGridApi] = useState<GridApi | null>(null);
  const [columnApi, setColumnApi] = useState<ColumnApi | null>(null);
  const [printing, setPrinting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [layoutLoaded, setLayoutLoaded] = useState(false);
  const [hasLayout, setHasLayout] = useState(false);

  // const [headerConfigs] = useState<ModalTableHeader[]>(convertToTableHeaders(headers));
  const headerConfigs: ModalTableHeader[] = convertToTableHeaders(headers);
  const gridOptions = getGridOptions();
  const agGrid = useRef<AgGridReact | null>(null);
  const resettingLayout = useRef(false);

  const helpers: string[] = [];
  const classes: string[] = [];
  const blocks: any[] = [];

  const usingAutoHeight = (headers as ModalTableHeader[]).find(h => h.autoHeight === true) !== undefined;

  const { options } = useContext<any>(ModalContext);

  const skipLayout = (view: string) => {
    // multiple tables
    // if (view === 'quoteCostToDate') {
    //   return true;
    // }

    return false;
  };

  const getLayoutKey = () => {
    return `ModalTableLayout:${options.view}${controlId}`;
  };

  const loadLayout = () => {
    if (skipLayout(options.view)) {
      return;
    }

    setLayoutLoaded(true);

    try {
      const layout = localStorage.getItem(getLayoutKey()) || '';
      if (!layout) {
        return;
      }

      setHasLayout(true);

      const { filterModel, columnState } = JSON.parse(layout);

      gridApi?.setFilterModel(filterModel);
      columnApi?.applyColumnState({ state: columnState, applyOrder: true });
    } catch (err) {
      console.log(err);
    }
  };

  const saveLayout = () => {
    if (skipLayout(options.view)) {
      return;
    }

    if (!layoutLoaded || resettingLayout.current) {
      return;
    }

    setHasLayout(true);

    localStorage.setItem(
      getLayoutKey(),
      JSON.stringify({
        filterModel: gridApi?.getFilterModel(),
        columnState: columnApi?.getColumnState(),
      })
    );
  };

  const debouncedSaveLayout = _.debounce(saveLayout, 500);

  const resetTable = () => {
    // resettingLayout prevents saveLayout from being triggered during this process
    resettingLayout.current = true;
    localStorage.setItem(getLayoutKey(), '');
    setHasLayout(false);
    columnApi?.resetColumnState();
    gridApi?.setFilterModel(null);
    setTimeout(() => (resettingLayout.current = false), 100);
  };

  useEffect(() => {
    if (gridApi && columnApi) {
      sizeGrid();
      prepareBottomRow();
    }
  }, [gridApi, columnApi]);

  useEffect(() => {
    if (gridApi && columnApi && !layoutLoaded) {
      loadLayout();
    }
  }, [gridApi, columnApi, layoutLoaded]);

  const print = (params: GridApi | void | null | undefined): void => {
    const grid = gridApi || params;

    if (!grid) {
      return;
    }

    const csvData = grid?.getDataAsCsv();

    setPrinting(true);
    ApiService.post('/print/csv2pdf', { data: csvData, title: options.title }).then((response: AxiosResponse) => {
      printJS({ printable: response.data.fileData, type: 'pdf', base64: true });
      setPrinting(false);
    });
  };

  const prepareBottomRow = (): void => {
    const bottomRow: any[] = [];
    headerConfigs.forEach((header: ModalTableHeader | string, index: number) => {
      if (typeof header === 'string') return;

      if (header.aggregate && header.text) {
        if (bottomRow.length) {
          bottomRow[0][header.text] = aggregate[header.aggregate](data.map(d => d[index]));
        } else {
          bottomRow.push({ [header.text]: aggregate[header.aggregate](data.map(d => d[index])) });
        }
      }
    });

    if (bottomRow.length) {
      // Set any default values
      headerConfigs.forEach((header: ModalTableHeader | string, index: number) => {
        if (typeof header === 'string') return;
        if (header.defaultValue && !header.aggregate && header.text) {
          bottomRow[0][header.text] = header.defaultValue;
        }
      });
      bottomRow[0][headerConfigs[0].text || ''] = 'Total';
      gridApi?.setPinnedBottomRowData(bottomRow);
    }
  };

  const exportTable = () => {
    setExporting(true);

    const title = options.title || 'TableExport';

    setTimeout(() => {
      gridApi?.exportDataAsCsv({
        fileName: `${title.replace(' ', '_')}-${moment().format('YYYY-MM-DD')}.csv`,
        prependContent: `${title}\n`,
      });
      setExporting(false);
    }, 1);
  };

  const headerDefinedWidth = (): boolean => {
    for (const config of headerConfigs) {
      if (config.width) {
        return true;
      }
    }

    return false;
  };

  const sizeGrid = () => {
    if (gridApi && !headerDefinedWidth()) {
      gridApi.sizeColumnsToFit();
    }
  };

  const onGridReady = (params: GridReadyEvent) => {
    setGridApi(params.api);
    setColumnApi(params.columnApi);
    sizeGrid();
  };

  const onColumnResized = (params: any) => {
    if (!usingAutoHeight) {
      params.api.resetRowHeights();
    }

    // On modal close a saveLayout with this event source gets triggered, resetting the layout to orig /shrug
    if (params.source === 'gridOptionsChanged') {
      return;
    }

    debouncedSaveLayout();
  };

  const onColumnVisible = (params: any) => {
    if (!usingAutoHeight) {
      params.api.resetRowHeights();
    }
  };

  const getColumnDefs = () => {
    const colDefs: ModalTableColumn[] = [];

    // Get current column state to backfill some userpref keys
    const columnState = columnApi?.getColumnState();

    headerConfigs.forEach((column: ModalTableHeader, columnIndex: number) => {
      const cellClass: string[] = [];
      const { helper, className, text, ...rest } = column;

      const headerText = text || '';
      helpers[columnIndex] = helper || '';
      classes[columnIndex] = className || 'default';

      if (className) {
        if (Array.isArray(className)) {
          cellClass.concat(className);
        } else {
          cellClass.push(className);
        }
      }

      if (!headerText) {
        return;
      }

      if (cellClass.includes('block')) {
        blocks.push(headerText);
      }

      const columnDef: ModalTableColumn = {
        headerName: headerText,
        field: headerText,
        columnIndex,
        cellClass,
        ...rest,
      };

      // Backfill userpref keys from current layout
      if (columnState && columnState.length) {
        const columnStateConfig = columnState.find(c => {
          return c.colId === columnDef.field;
        });

        if (columnStateConfig) {
          ['width'].forEach(k => (columnDef[k] = columnStateConfig[k]));
        }
      }

      if (helper) {
        columnDef.cellRendererFramework = (params: ICellRendererParams | any) => {
          const { columnIndex } = params.colDef;

          return (
            <a
              onClick={() => {
                modal.open(helpers[columnIndex], {
                  lookupKey: params.value,
                  rowData: params.node.data,
                });
              }}
              className="dd"
            >
              {params.value}
            </a>
          );
        };
      }

      colDefs.push(columnDef);
    });

    return colDefs;
  };

  const getRowData = () => {
    return data.map(row => {
      const rowObject = {};
      row.forEach((item, index) => {
        if (headerConfigs[index]) {
          const key = headerConfigs[index].text;
          const value = item;

          if (key) {
            if (types.some(t => t === headerConfigs[index].type)) {
              rowObject[key] = Validator.sanitize(headerConfigs[index].type, value);
            } else {
              rowObject[key] = value;
            }
          }
        }
      });
      return rowObject;
    });
  };

  const isDataEmpty = () => {
    // TODO We shouldn't have to do this; an empty array is ideal
    if (!data || !data.length || !data[0].length || data[0][0] === 'NO.DATA') {
      return true;
    }

    return false;
  };

  const getContextMenuItems = (params: GetContextMenuItemsParams) => {
    return [
      'copy',
      'copyWithHeaders',
      'separator',
      'export',
      'separator',
      { icon: '<i class="fas fa-print"></i>', name: 'Print', action: () => print(params?.api) },
    ];
  };

  const tableStyle = {
    height: '300px',
    ...style,
  };

  if (isDataEmpty() || !headerConfigs.length) {
    return (
      <div className="loading-wrapper empty-table">
        <div className="loading-child">{emptyMessage}</div>
      </div>
    );
  }

  return (
    <div className="modal-table">
      {hideToolbar === false && (
        <div className="mb-2 pt-1 pl-1">
          <Button
            variant="secondary"
            onClick={() => print()}
            disabled={printing}
            style={{ marginRight: '5px' }}
            size="sm"
          >
            {printing ? 'Please Wait' : 'Print Table'}
          </Button>
          <Button variant="secondary" onClick={exportTable} disabled={exporting} size="sm">
            {exporting ? 'Please Wait' : 'Export'}
          </Button>
          <Button
            disabled={!hasLayout}
            variant="secondary"
            className="float-right mb-2 mr-1"
            size="sm"
            onClick={resetTable}
          >
            Reset Table
          </Button>
        </div>
      )}
      <div id="modal-grid-wrapper" style={tableStyle} className="ag-theme-balham">
        <AgGridReact
          ref={agGrid}
          columnDefs={getColumnDefs()}
          gridOptions={{ ...gridOptions, maintainColumnOrder: true }}
          getContextMenuItems={getContextMenuItems}
          modules={AllModules}
          onGridReady={onGridReady}
          rowData={getRowData()}
          onColumnResized={onColumnResized}
          onColumnVisible={onColumnVisible}
          onColumnMoved={debouncedSaveLayout}
          onSortChanged={debouncedSaveLayout}
          onFilterChanged={debouncedSaveLayout}
          suppressPropertyNamesCheck
        />
      </div>
    </div>
  );
};

ModalTable.propTypes = {
  data: PropTypes.array,
  headers: PropTypes.array,
  hideToolbar: PropTypes.bool,
  style: PropTypes.object,
  emptyMessage: PropTypes.string,
};

export default ModalTable;
